// A demo plan's own delete control: in the plan header, beside the `.demoMark` — see
// `PlanScreen.tsx` for where it is mounted.
//
// AN IN-PAGE CONFIRMATION, NOT `window.confirm`, which blocks the whole page and cannot
// be styled: the shared `ConfirmDialog`, a native `<dialog>` in the top layer, centred on
// the viewport.
//
// THE SERVER IS THE ONLY GATE. This control renders for every demo plan and says nothing
// about who started it — nothing here could, since the caller's own account is not on the
// wire (see `contract.ts`'s `DemoSource`). A person invited into someone else's demo sees
// the same button, and trying it gets the server's ordinary refusal
// (`@mor/api`'s `routes.ts`), which reads here as the same generic "try again" every other
// failure in this client gets — this package enforces nothing, the same rule
// `client.ts`'s own header states for the whole client.

import { useState } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';
import { ConfirmDialog } from './ConfirmDialog.js';

export type DemoDeleteControlProps = {
  client: ApiClient;
  planId: string;
  /** Called only once the server has actually removed the plan. */
  onDeleted: () => void;
};

export function DemoDeleteControl({
  client,
  planId,
  onDeleted,
}: DemoDeleteControlProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);

  return (
    <span className="demoDelete">
      <button
        type="button"
        className="demoDelete__button"
        aria-label="Delete this demo"
        onClick={() => setConfirming(true)}
      >
        Delete
      </button>
      {confirming ? (
        <ConfirmDialog
          title="Delete this demo?"
          name="This demo"
          detail="This cannot be undone."
          confirmLabel="Delete"
          onConfirm={async () => {
            // The plan survives a refusal — this control only reports success through
            // `onDeleted` — and the dialog stays open so the message renders right next to
            // the button that caused it.
            await client.deleteDemoPlan(planId);
            onDeleted();
          }}
          onCancel={() => setConfirming(false)}
          problemFor={(error) =>
            // Every refusal this route gives is a 404, indistinguishable from an unknown
            // address on purpose: not yours, not a demo, demo mode off. So the client cannot
            // say WHICH — but it can say the one thing that is true of all three, which is
            // that it will not start working. "Try again" was a lie about a permanent no.
            error instanceof ApiFailure && error.status === 404
              ? 'This demo can’t be deleted from here.'
              : 'That didn’t delete. Try again.'
          }
        />
      ) : null}
    </span>
  );
}
